import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  Snackbar
} from '@mui/material';
import {
  Close,
  ContentCopy,
  Visibility,
  VisibilityOff,
  Info,
  Warning
} from '@mui/icons-material';
import SSOConfigCompactDetails from './SSOConfigCompactDetails';

interface SSODetailsSmallDialogProps {
  open: boolean;
  onClose: () => void;
  application: {
    id: string;
    name: string;
    azure_metadata_url?: string;
    idp_initiated_url?: string;
    certificate_expiration_date?: string;
    sso_type?: string;
    platform?: string;
    entity_id?: string;
    acs_url?: string;
    name_id_format?: string;
    client_id?: string;
    client_secret?: string;
    oidc_discovery_endpoint?: string;
    grant_type?: string;
    redirect_uris?: string[];
    scopes?: string[];
    attribute_statements?: any[];
    okta_app_id?: string;
    entra_app_id?: string;
    sso_config?: any;
    review_notes?: string;
  };
}

const SSODetailsSmallDialog: React.FC<SSODetailsSmallDialogProps> = ({
  open,
  onClose,
  application
}) => {
  const [showSecret, setShowSecret] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const cfg = application.sso_config || {};
  const ssoType = (cfg.sso_type || application.sso_type || '').toUpperCase();
  const platform = String(cfg.platform || application.platform || '').toLowerCase();
  const isOkta = platform === 'okta';
  const metadataUrl = application.azure_metadata_url || cfg.azure_metadata_url || cfg.metadata_url;
  const oktaAppId = application.okta_app_id || cfg.okta_app_id || cfg.manual_config?.okta_app_id;
  const entityId = application.entity_id || cfg.entity_id;
  const acsUrl = application.acs_url || cfg.acs_url;
  const clientId = application.client_id || cfg.client_id || cfg.manual_config?.client_id;
  const clientSecret = application.client_secret || cfg.client_secret;
  const discovery = application.oidc_discovery_endpoint || cfg.oidc_discovery_endpoint || cfg.manual_config?.oidc_discovery_endpoint;
  const redirectUris = application.redirect_uris?.length ? application.redirect_uris : (cfg.redirect_uris || []);
  const scopes = application.scopes?.length ? application.scopes : (cfg.scopes || []);

  // Prefer compact details when full config is present
  const displayConfig = {
    ...cfg,
    connection_name: cfg.connection_name,
    platform: cfg.platform || application.platform,
    sso_type: cfg.sso_type || application.sso_type,
    status: cfg.status,
    entity_id: entityId,
    acs_url: acsUrl,
    name_id_format: application.name_id_format || cfg.name_id_format,
    client_id: clientId,
    client_secret: clientSecret,
    grant_type: application.grant_type || cfg.grant_type,
    redirect_uris: redirectUris,
    scopes,
    oidc_discovery_endpoint: discovery,
    azure_metadata_url: metadataUrl,
    idp_initiated_url: application.idp_initiated_url || cfg.idp_initiated_url,
    certificate_expiration_date: application.certificate_expiration_date || cfg.certificate_expiration_date,
    attribute_statements: application.attribute_statements || cfg.attribute_statements || [],
    attributes_to_release: cfg.attributes_to_release,
    okta_app_id: oktaAppId,
    entra_app_id: application.entra_app_id || cfg.entra_app_id || cfg.manual_config?.entra_app_id,
    review_notes: application.review_notes || cfg.review_notes,
    business_justification: cfg.business_justification,
    technical_notes: cfg.technical_notes,
    data_classification: cfg.data_classification,
    contact_email: cfg.contact_email,
    expected_user_count: cfg.expected_user_count,
    manual_config: cfg.manual_config,
  };

  const handleCopy = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    setCopiedField(null);
  };

  const formatExpirationDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const isSecretExpiringSoon = (dateString?: string) => {
    if (!dateString) return false;
    try {
      const expirationDate = new Date(dateString);
      const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      return expirationDate <= thirtyDaysFromNow;
    } catch {
      return false;
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {ssoType} Configuration — {cfg.connection_name ? `sso-${cfg.connection_name}` : application.name}
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Success!</strong> Your {ssoType} application
                {isOkta ? ' is provisioned in Okta' : ' is configured'}.
                Review the credentials below for integration.
              </Typography>
            </Alert>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
                Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">SSO Type</Typography>
                  <Chip label={ssoType || 'Unknown'} color="primary" size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Platform</Typography>
                  <Chip
                    label={isOkta ? 'Okta' : (platform || '—')}
                    color={isOkta ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Typography variant="body2">{cfg.status || '—'}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          {/* Full structured details (includes Okta app id, attributes, metadata) */}
          <SSOConfigCompactDetails config={displayConfig} />

          {/* Copyable credential fields for SAML/OIDC */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Copyable Credentials</Typography>
          <Grid container spacing={2}>
            {ssoType === 'SAML' && (
              <>
                {entityId && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Entity ID</Typography>
                    <TextField
                      fullWidth
                      value={entityId}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy Entity ID">
                              <IconButton onClick={() => handleCopy(entityId, 'entityId')} edge="end">
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                )}
                {acsUrl && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>ACS URL</Typography>
                    <TextField
                      fullWidth
                      value={acsUrl}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy ACS URL">
                              <IconButton onClick={() => handleCopy(acsUrl, 'acsUrl')} edge="end">
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                )}
                {metadataUrl && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      {isOkta ? 'Okta Metadata URL' : 'IdP Metadata URL'}
                    </Typography>
                    <TextField
                      fullWidth
                      value={metadataUrl}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy Metadata URL">
                              <IconButton onClick={() => handleCopy(metadataUrl, 'metadataUrl')} edge="end">
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                )}
                {oktaAppId && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Okta App ID</Typography>
                    <TextField
                      fullWidth
                      value={oktaAppId}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy Okta App ID">
                              <IconButton onClick={() => handleCopy(oktaAppId, 'oktaAppId')} edge="end">
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                )}
              </>
            )}

            {ssoType === 'OIDC' && (
              <>
                {clientId && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Client ID</Typography>
                    <TextField
                      fullWidth
                      value={clientId}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy Client ID">
                              <IconButton onClick={() => handleCopy(clientId, 'clientId')} edge="end">
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                )}
                {clientSecret && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Client Secret</Typography>
                    <TextField
                      fullWidth
                      type={showSecret ? 'text' : 'password'}
                      value={clientSecret}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={showSecret ? 'Hide Secret' : 'Show Secret'}>
                              <IconButton onClick={() => setShowSecret(!showSecret)} edge="end">
                                {showSecret ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy Secret">
                              <IconButton onClick={() => handleCopy(clientSecret, 'clientSecret')} edge="end">
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      size="small"
                    />
                    {cfg.client_secret_expires_at && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Expires: {formatExpirationDate(cfg.client_secret_expires_at)}
                        </Typography>
                        {isSecretExpiringSoon(cfg.client_secret_expires_at) && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
                            This secret expires soon! Please generate a new one before the expiration date.
                          </Alert>
                        )}
                      </Box>
                    )}
                  </Grid>
                )}
                {discovery && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>OIDC Discovery Endpoint</Typography>
                    <TextField
                      fullWidth
                      value={discovery}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy Discovery Endpoint">
                              <IconButton onClick={() => handleCopy(discovery, 'discoveryEndpoint')} edge="end">
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                )}
                {oktaAppId && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Okta App ID</Typography>
                    <TextField
                      fullWidth
                      value={oktaAppId}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy Okta App ID">
                              <IconButton onClick={() => handleCopy(oktaAppId, 'oktaAppId')} edge="end">
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                )}
                {redirectUris.map((uri: string, index: number) => (
                  <Grid item xs={12} key={index}>
                    <Typography variant="subtitle2" gutterBottom>
                      {index === 0 ? 'Redirect URIs' : ''}
                    </Typography>
                    <TextField
                      fullWidth
                      value={uri}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy Redirect URI">
                              <IconButton onClick={() => handleCopy(uri, `redirectUri${index}`)} edge="end">
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                ))}
              </>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Got it!
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        message={`${copiedField === 'clientId' ? 'Client ID' :
                  copiedField === 'clientSecret' ? 'Client Secret' :
                  copiedField === 'entityId' ? 'Entity ID' :
                  copiedField === 'acsUrl' ? 'ACS URL' :
                  copiedField === 'metadataUrl' ? 'Metadata URL' :
                  copiedField === 'discoveryEndpoint' ? 'Discovery Endpoint' :
                  copiedField === 'oktaAppId' ? 'Okta App ID' :
                  'Configuration'} copied to clipboard!`}
      />
    </>
  );
};

export default SSODetailsSmallDialog;
